import React, { useCallback, useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, XIcon, SmartphoneIcon, ShieldCheckIcon, GlobeIcon, CoffeeIcon, FlameIcon, TreesIcon, LeafIcon, ZapIcon, CheckCircle2Icon, ArrowUpRightIcon, BarChart3Icon, UsersIcon, MapPinIcon, FileTextIcon } from 'lucide-react';
import { MAILTO } from '../utils/mailto';
interface PresentationProps {
  onExit: () => void;
}
const TOTAL_SLIDES = 9;
/* ─── Decorative SVG Patterns ─── */
function GridPattern({
  id,
  opacity = 0.04
}: {
  id: string;
  opacity?: number;
}) {
  return <svg className="absolute inset-0 w-full h-full" style={{
    opacity
  }} aria-hidden="true" data-id="element-2261">
      <defs data-id="element-2262">
        <pattern id={id} width="60" height="60" patternUnits="userSpaceOnUse" patternTransform="rotate(45)" data-id="element-2263">
          <line x1="0" y1="0" x2="0" y2="60" stroke="currentColor" strokeWidth="0.5" data-id="element-2264" />
          <line x1="0" y1="0" x2="60" y2="0" stroke="currentColor" strokeWidth="0.5" data-id="element-2265" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} data-id="element-2266" />
    </svg>;
}
function HexagonDecoration({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 200 200" className={className} aria-hidden="true" data-id="element-2267">
      <polygon points="100,10 180,55 180,145 100,190 20,145 20,55" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.15" data-id="element-2268" />
      <polygon points="100,35 160,70 160,130 100,165 40,130 40,70" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.1" data-id="element-2269" />
      <polygon points="100,60 140,85 140,115 100,140 60,115 60,85" fill="currentColor" fillOpacity="0.05" stroke="currentColor" strokeWidth="1.5" opacity="0.2" data-id="element-2270" />
      <circle cx="100" cy="10" r="3" fill="currentColor" opacity="0.3" data-id="element-2271" />
      <circle cx="180" cy="55" r="2.5" fill="currentColor" opacity="0.2" data-id="element-2272" />
      <circle cx="180" cy="145" r="3" fill="currentColor" opacity="0.3" data-id="element-2273" />
      <circle cx="100" cy="190" r="2.5" fill="currentColor" opacity="0.2" data-id="element-2274" />
      <circle cx="20" cy="145" r="3" fill="currentColor" opacity="0.3" data-id="element-2275" />
      <circle cx="20" cy="55" r="2.5" fill="currentColor" opacity="0.2" data-id="element-2276" />
    </svg>;
}
function CircuitDecoration({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 300 300" className={className} aria-hidden="true" data-id="element-2277">
      <circle cx="150" cy="150" r="120" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.08" data-id="element-2278" />
      <circle cx="150" cy="150" r="80" fill="none" stroke="currentColor" strokeWidth="0.6" opacity="0.06" data-id="element-2279" />
      <circle cx="150" cy="150" r="40" fill="currentColor" fillOpacity="0.03" stroke="currentColor" strokeWidth="1" opacity="0.1" data-id="element-2280" />
      <line x1="150" y1="30" x2="150" y2="70" stroke="currentColor" strokeWidth="0.8" opacity="0.1" data-id="element-2281" />
      <line x1="270" y1="150" x2="230" y2="150" stroke="currentColor" strokeWidth="0.8" opacity="0.1" data-id="element-2282" />
      <line x1="150" y1="270" x2="150" y2="230" stroke="currentColor" strokeWidth="0.8" opacity="0.1" data-id="element-2283" />
      <line x1="30" y1="150" x2="70" y2="150" stroke="currentColor" strokeWidth="0.8" opacity="0.1" data-id="element-2284" />
      <circle cx="150" cy="30" r="4" fill="currentColor" opacity="0.15" data-id="element-2285" />
      <circle cx="270" cy="150" r="4" fill="currentColor" opacity="0.15" data-id="element-2286" />
      <circle cx="150" cy="270" r="4" fill="currentColor" opacity="0.15" data-id="element-2287" />
      <circle cx="30" cy="150" r="4" fill="currentColor" opacity="0.15" data-id="element-2288" />
    </svg>;
}
/* ─── Slide Components ─── */
function SlideWrapper({
  children,
  bg,
  textColor = 'text-carbon-deep',
  dark = false,
  hideWatermark = false
}: {
  children: React.ReactNode;
  bg: string;
  textColor?: string;
  dark?: boolean;
  hideWatermark?: boolean;
}) {
  return <div className={`relative w-full h-full flex items-center justify-center overflow-hidden ${bg} ${textColor}`} data-id="element-2289">
      {children}
      {/* Logo watermark — bottom right of every slide (unless hidden) */}
      {!hideWatermark && <img src="https://f000.backblazeb2.com/file/jpgshared/f2RRyoGc" alt="" aria-hidden="true" className="absolute bottom-6 right-6 h-10 w-auto pointer-events-none select-none z-20" style={{
      opacity: 1
    }} data-id="element-2290" />}
    </div>;
}
/* Slide 1: Title */
function SlideTitleContent() {
  return <SlideWrapper bg="bg-white" dark={false} hideWatermark data-id="element-2291">
      <GridPattern id="title-grid" opacity={0.03} data-id="element-2292" />
      <HexagonDecoration className="absolute -right-16 -top-16 w-80 h-80 text-carbon-bright" data-id="element-2293" />
      <HexagonDecoration className="absolute -left-20 -bottom-20 w-96 h-96 text-carbon-bright" data-id="element-2294" />

      <div className="relative z-10 text-center max-w-4xl px-8" data-id="element-2295">
        <motion.img src="https://f000.backblazeb2.com/file/jpgshared/f2RRyoGc" alt="CarbonSafe" className="h-14 sm:h-16 mx-auto mb-10" initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2296" />
        <motion.h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-carbon-deep tracking-tight leading-[1.08]" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2297">
          Verified supply chains
          <br data-id="element-2298" />
          <span className="text-carbon-bright" data-id="element-2299">from Africa's farms</span>
          <br data-id="element-2300" />
          to global markets
        </motion.h1>
        <motion.p className="mt-8 text-lg sm:text-xl text-carbon-mid max-w-2xl mx-auto leading-relaxed" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2301">
          Independent dMRV-as-a-Service for Africa–EU trade and finance
          compliance
        </motion.p>
        <motion.div className="mt-8 flex flex-wrap items-center justify-center gap-3" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} data-id="element-2302">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-2303">
            <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2304" />
            EUDR Compliant
          </span>
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-2305">
            <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2306" />
            Verra VCS
          </span>
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-carbon-geo bg-white text-sm font-medium text-carbon-deep" data-id="element-2307">
            <span className="w-2 h-2 rounded-full bg-carbon-bright" data-id="element-2308" />
            Gold Standard
          </span>
        </motion.div>
      </div>
    </SlideWrapper>;
}
/* Slide 2: The Problem */
function SlideProblemContent() {
  return <SlideWrapper bg="bg-carbon-deep" textColor="text-white" dark data-id="element-2309">
      <GridPattern id="problem-grid" opacity={0.03} data-id="element-2310" />
      {/* Background photo overlay */}
      <div className="absolute inset-0 z-0" data-id="element-2311">
        <img src="https://images.unsplash.com/photo-1589923188651-268a9765e432?w=1600&h=900&fit=crop&crop=center" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-2312" />
        <div className="absolute inset-0 bg-carbon-deep/90" data-id="element-2313" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-8" data-id="element-2314">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-2315">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2316">
            <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-2317">
              The Problem
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.1]" data-id="element-2318">
              Africa's supply chains are
              <span className="text-carbon-bright" data-id="element-2319"> invisible</span> to global
              markets
            </h2>
            <p className="mt-6 text-lg text-white/60 leading-relaxed" data-id="element-2320">
              1.7 million Ugandan smallholders produce $1.3B+ in exports
              annually — but without verifiable data, they can't access premium
              markets, carbon finance, or EU trade compliance.
            </p>
          </motion.div>

          <motion.div className="space-y-5" initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2321">
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-2322">
              <p className="text-3xl font-bold text-red-400" data-id="element-2323">$900M+</p>
              <p className="mt-1 text-sm text-white/50" data-id="element-2324">
                Uganda's coffee exports to the EU — at risk without EUDR
                compliance
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-2325">
              <p className="text-3xl font-bold text-amber-400" data-id="element-2326">95%</p>
              <p className="mt-1 text-sm text-white/50" data-id="element-2327">
                Of households use biomass cooking — unverified emission
                reductions
              </p>
            </div>
            <div className="p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm" data-id="element-2328">
              <p className="text-3xl font-bold text-carbon-bright" data-id="element-2329">90K ha/yr</p>
              <p className="mt-1 text-sm text-white/50" data-id="element-2330">
                Forest lost annually — REDD+ credits require rigorous dMRV
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 3: The Solution */
function SlideSolutionContent() {
  return <SlideWrapper bg="bg-white" dark={false} data-id="element-2331">
      <GridPattern id="solution-grid" opacity={0.03} data-id="element-2332" />
      <HexagonDecoration className="absolute -left-12 top-12 w-64 h-64 text-carbon-bright" data-id="element-2333" />

      <div className="relative z-10 max-w-5xl mx-auto px-8" data-id="element-2334">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-2335">
          <motion.div initial={{
          opacity: 0,
          x: -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2336">
            <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-4" data-id="element-2337">
              The Solution
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-2338">
              A mobile-first
              <span className="text-carbon-bright" data-id="element-2339"> dMRV platform</span>
            </h2>
            <p className="mt-6 text-lg text-carbon-mid leading-relaxed" data-id="element-2340">
              Farmers use USSD or our mobile app to log harvest data. CarbonSafe
              generates Digital Quality Certificates that international buyers
              and carbon registries trust.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-id="element-2341">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-carbon-bright/5 border border-carbon-geo" data-id="element-2342">
                <SmartphoneIcon className="w-5 h-5 text-carbon-bright" data-id="element-2343" />
                <span className="text-sm font-semibold text-carbon-deep" data-id="element-2344">
                  USSD + App
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-carbon-bright/5 border border-carbon-geo" data-id="element-2345">
                <MapPinIcon className="w-5 h-5 text-carbon-bright" data-id="element-2346" />
                <span className="text-sm font-semibold text-carbon-deep" data-id="element-2347">
                  GPS-Tagged
                </span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-carbon-bright/5 border border-carbon-geo" data-id="element-2348">
                <ShieldCheckIcon className="w-5 h-5 text-carbon-bright" data-id="element-2349" />
                <span className="text-sm font-semibold text-carbon-deep" data-id="element-2350">
                  Tamper-Proof
                </span>
              </div>
            </div>
          </motion.div>

          {/* Farmer with phone photo + certificate overlay */}
          <motion.div initial={{
          opacity: 0,
          x: 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} className="relative" data-id="element-2351">
            <div className="overflow-hidden shadow-xl" data-id="element-2352">
              <img src="https://static.vecteezy.com/system/resources/previews/053/560/533/large_2x/female-african-farmer-using-her-phone-photo.jpg" alt="African farmer using mobile phone in field" className="w-full h-[340px] object-cover" data-id="element-2353" />
            </div>
            {/* Floating certificate badge */}
            <div className="absolute -bottom-4 -left-4 bg-white rounded-xl border border-carbon-geo shadow-lg p-3 max-w-[200px]" data-id="element-2354">
              <div className="flex items-center gap-2 mb-2" data-id="element-2355">
                <div className="w-6 h-6 rounded-md bg-carbon-bright flex items-center justify-center" data-id="element-2356">
                  <span className="text-white text-[8px] font-bold" data-id="element-2357">CS</span>
                </div>
                <span className="text-[10px] font-bold text-carbon-deep" data-id="element-2358">
                  Digital Certificate
                </span>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-2359">
                <CheckCircle2Icon className="w-3.5 h-3.5 text-carbon-bright" data-id="element-2360" />
                <span className="text-[10px] font-semibold text-carbon-bright" data-id="element-2361">
                  Verified & Compliant
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 4: How It Works */
function SlideHowItWorksContent() {
  const steps = [{
    icon: SmartphoneIcon,
    num: '01',
    title: 'Capture',
    desc: 'Farmers log data via USSD or mobile app — GPS-tagged, timestamped, tamper-proof.'
  }, {
    icon: BarChart3Icon,
    num: '02',
    title: 'Verify',
    desc: 'AI models cross-reference satellite imagery, field data, and sensor readings.'
  }, {
    icon: FileTextIcon,
    num: '03',
    title: 'Certify',
    desc: 'Digital Quality Certificates issued with full compliance documentation.'
  }, {
    icon: GlobeIcon,
    num: '04',
    title: 'Trade',
    desc: 'Certificates unlock EU market access, carbon credits, and trade finance.'
  }];
  return <SlideWrapper bg="bg-carbon-deep" textColor="text-white" dark data-id="element-2362">
      <GridPattern id="how-grid" opacity={0.03} data-id="element-2363" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-2364">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2365">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-2366">
            How It Works
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-id="element-2367">
            Four steps to{' '}
            <span className="text-carbon-bright" data-id="element-2368">verified impact</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2369">
          {steps.map((step, i) => <motion.div key={step.num} className="relative p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.15 + i * 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2370">
              <span className="text-[10px] font-bold text-carbon-bright/50 uppercase tracking-widest" data-id="element-2371">
                {step.num}
              </span>
              <div className="w-12 h-12 rounded-xl bg-carbon-bright/15 flex items-center justify-center mt-3 mb-4" data-id="element-2372">
                <step.icon className="w-6 h-6 text-carbon-bright" data-id="element-2373" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-2374">
                {step.title}
              </h3>
              <p className="text-sm text-white/50 leading-relaxed" data-id="element-2375">
                {step.desc}
              </p>
              {i < 3 && <div className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 z-20" data-id="element-2376">
                  <ArrowRightIcon className="w-5 h-5 text-carbon-bright/30" data-id="element-2377" />
                </div>}
            </motion.div>)}
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 5: Verticals */
function SlideVerticalsContent() {
  const verticals = [{
    icon: CoffeeIcon,
    title: 'Coffee Traceability',
    desc: 'Farm-to-cup EUDR compliance',
    color: 'bg-amber-500/10 text-amber-600',
    image: 'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=300&h=200&fit=crop&crop=center'
  }, {
    icon: FlameIcon,
    title: 'Cookstove Credits',
    desc: 'IoT-verified emission reductions',
    color: 'bg-orange-500/10 text-orange-600',
    image: 'https://www.engineeringforchange.org/wp-content/uploads/2018/02/ace1stove-1_resized.jpg'
  }, {
    icon: TreesIcon,
    title: 'Forestry & REDD+',
    desc: 'Satellite-verified forest carbon',
    color: 'bg-emerald-500/10 text-emerald-600',
    image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=300&h=200&fit=crop&crop=center'
  }, {
    icon: LeafIcon,
    title: 'Agroforestry',
    desc: 'Soil carbon sequestration',
    color: 'bg-green-500/10 text-green-600',
    image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=300&h=200&fit=crop&crop=center'
  }, {
    icon: ZapIcon,
    title: 'Renewable Energy',
    desc: 'Clean energy carbon credits',
    color: 'bg-blue-500/10 text-blue-600',
    image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=300&h=200&fit=crop&crop=center'
  }];
  return <SlideWrapper bg="bg-white" dark={false} data-id="element-2378">
      <GridPattern id="vert-grid" opacity={0.03} data-id="element-2379" />
      <HexagonDecoration className="absolute -right-16 -bottom-16 w-72 h-72 text-carbon-bright" data-id="element-2380" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-2381">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2382">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-2383">
            dMRV Verticals
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-2384">
            Five sectors,{' '}
            <span className="text-carbon-bright" data-id="element-2385">one platform</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-2386">
          {verticals.map((v, i) => <motion.div key={v.title} className="border border-carbon-geo bg-white overflow-hidden text-center" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1 + i * 0.08,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2387">
              <img src={v.image} alt={v.title} className="w-full h-24 object-cover" data-id="element-2388" />
              <div className="p-4" data-id="element-2389">
                <div className={`w-10 h-10 rounded-xl ${v.color} flex items-center justify-center mx-auto mb-3 -mt-8 relative z-10 border-2 border-white shadow-sm`} data-id="element-2390">
                  <v.icon className="w-5 h-5" data-id="element-2391" />
                </div>
                <h3 className="text-sm font-bold text-carbon-deep mb-1" data-id="element-2392">
                  {v.title}
                </h3>
                <p className="text-xs text-carbon-mid leading-snug" data-id="element-2393">{v.desc}</p>
              </div>
            </motion.div>)}
        </div>

        <motion.p className="text-center mt-8 text-base text-carbon-mid" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} data-id="element-2394">
          Each vertical uses the same core platform — adapted with
          sector-specific methodologies and compliance frameworks.
        </motion.p>
      </div>
    </SlideWrapper>;
}
/* Slide 6: Market Opportunity */
function SlideMarketContent() {
  return <SlideWrapper bg="bg-carbon-deep" textColor="text-white" dark data-id="element-2395">
      <GridPattern id="market-grid" opacity={0.03} data-id="element-2396" />
      {/* Background photo */}
      <div className="absolute inset-0 z-0" data-id="element-2397">
        <img src="https://images.unsplash.com/photo-1523741543316-beb7fc7023d8?w=1600&h=900&fit=crop&crop=center" alt="" aria-hidden="true" className="w-full h-full object-cover" data-id="element-2398" />
        <div className="absolute inset-0 bg-carbon-deep/92" data-id="element-2399" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-2400">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2401">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-2402">
            Market Opportunity
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-id="element-2403">
            The numbers speak for{' '}
            <span className="text-carbon-bright" data-id="element-2404">themselves</span>
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2405">
          <motion.div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.15,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2406">
            <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-2407">
              1.7M+
            </p>
            <p className="mt-2 text-sm font-semibold text-white/70" data-id="element-2408">
              Smallholder Farmers
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-2409">
              Across Uganda's growing regions
            </p>
          </motion.div>

          <motion.div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2410">
            <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-2411">
              $1.3B+
            </p>
            <p className="mt-2 text-sm font-semibold text-white/70" data-id="element-2412">
              Annual Exports
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-2413">
              Uganda's agricultural export value
            </p>
          </motion.div>

          <motion.div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2414">
            <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-2415">
              $735M+
            </p>
            <p className="mt-2 text-sm font-semibold text-white/70" data-id="element-2416">
              Carbon Credit Potential
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-2417">
              Across all five verticals annually
            </p>
          </motion.div>

          <motion.div className="text-center p-6 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.45,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2418">
            <p className="text-4xl sm:text-5xl font-bold text-carbon-bright" data-id="element-2419">
              Dec '25
            </p>
            <p className="mt-2 text-sm font-semibold text-white/70" data-id="element-2420">
              EUDR Deadline
            </p>
            <p className="mt-1 text-xs text-white/40" data-id="element-2421">EU enforcement begins</p>
          </motion.div>
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 7: Platform */
function SlidePlatformContent() {
  return <SlideWrapper bg="bg-white" dark={false} data-id="element-2422">
      <GridPattern id="platform-grid" opacity={0.03} data-id="element-2423" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-2424">
        <motion.div className="text-center mb-10" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2425">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-2426">
            The Platform
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-carbon-deep tracking-tight" data-id="element-2427">
            One dashboard for{' '}
            <span className="text-carbon-bright" data-id="element-2428">everything</span>
          </h2>
        </motion.div>

        <motion.div className="rounded-2xl shadow-2xl border border-carbon-geo overflow-hidden bg-white" style={{
        transform: 'perspective(1200px) rotateX(2deg) scale(0.92)'
      }} initial={{
        opacity: 0,
        y: 50
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2429">
          <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 border-b border-gray-200" data-id="element-2430">
            <div className="flex gap-1.5" data-id="element-2431">
              <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-2432" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-2433" />
              <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-2434" />
            </div>
            <div className="flex-1 mx-4" data-id="element-2435">
              <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 font-mono border border-gray-200 max-w-md" data-id="element-2436">
                app.carbonsafe.io/dashboard
              </div>
            </div>
          </div>
          <div className="flex min-h-[280px] sm:min-h-[320px]" data-id="element-2437">
            <div className="w-40 bg-carbon-deep flex-shrink-0 p-3 hidden sm:block" data-id="element-2438">
              <div className="flex items-center gap-2 px-2 py-2 mb-4" data-id="element-2439">
                <div className="w-6 h-6 rounded-lg bg-carbon-bright flex items-center justify-center" data-id="element-2440">
                  <span className="text-white text-[9px] font-bold" data-id="element-2441">CS</span>
                </div>
                <span className="text-white text-xs font-bold" data-id="element-2442">CarbonSafe</span>
              </div>
              <nav className="space-y-0.5 text-[11px]" data-id="element-2443">
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/10 text-white font-medium" data-id="element-2444">
                  <BarChart3Icon className="w-3 h-3" data-id="element-2445" /> Overview
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-2446">
                  <CoffeeIcon className="w-3 h-3" data-id="element-2447" /> Coffee
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-2448">
                  <FlameIcon className="w-3 h-3" data-id="element-2449" /> Cookstoves
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-2450">
                  <TreesIcon className="w-3 h-3" data-id="element-2451" /> Forestry
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-2452">
                  <LeafIcon className="w-3 h-3" data-id="element-2453" /> Agroforestry
                </div>
                <div className="flex items-center gap-2 px-2 py-1.5 text-white/40" data-id="element-2454">
                  <ZapIcon className="w-3 h-3" data-id="element-2455" /> Energy
                </div>
              </nav>
            </div>
            <div className="flex-1 p-4 bg-gray-50" data-id="element-2456">
              <div className="flex items-center justify-between mb-3" data-id="element-2457">
                <div data-id="element-2458">
                  <h3 className="text-xs font-bold text-carbon-deep" data-id="element-2459">
                    All Verticals Overview
                  </h3>
                  <p className="text-[10px] text-gray-400" data-id="element-2460">
                    Last synced 3 min ago
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold" data-id="element-2461">
                  <span className="w-1 h-1 rounded-full bg-emerald-500" data-id="element-2462" /> All
                  Systems Online
                </span>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-3" data-id="element-2463">
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-2464">
                  <p className="text-[9px] text-gray-400" data-id="element-2465">Farms Registered</p>
                  <p className="text-base font-bold text-carbon-deep" data-id="element-2466">17,127</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-2467">
                  <p className="text-[9px] text-gray-400" data-id="element-2468">Certificates</p>
                  <p className="text-base font-bold text-carbon-bright" data-id="element-2469">
                    4,892
                  </p>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-2470">
                  <p className="text-[9px] text-gray-400" data-id="element-2471">tCO₂e Verified</p>
                  <p className="text-base font-bold text-carbon-deep" data-id="element-2472">28,400</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-2473">
                  <p className="text-[9px] text-gray-400" data-id="element-2474">Credit Value</p>
                  <p className="text-base font-bold text-carbon-deep" data-id="element-2475">$568K</p>
                </div>
                <div className="bg-white rounded-lg p-2.5 border border-gray-100" data-id="element-2476">
                  <p className="text-[9px] text-gray-400" data-id="element-2477">Compliance</p>
                  <p className="text-base font-bold text-carbon-bright" data-id="element-2478">
                    98.7%
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100" data-id="element-2479">
                <p className="text-[10px] font-semibold text-carbon-deep mb-2" data-id="element-2480">
                  Monthly Verified Impact
                </p>
                <div className="flex items-end gap-1.5 h-16" data-id="element-2481">
                  {[35, 48, 42, 65, 58, 72, 55, 80, 68, 85, 78, 92].map((h, i) => <div key={i} className="flex-1" data-id="element-2482">
                        <div className="w-full rounded-t-sm" style={{
                    height: `${h}%`,
                    backgroundColor: i >= 9 ? '#52B788' : i >= 6 ? '#6BCB77' : '#B7DFCA'
                  }} data-id="element-2483" />
                      </div>)}
                </div>
                <div className="flex justify-between mt-1" data-id="element-2484">
                  <span className="text-[8px] text-gray-400" data-id="element-2485">Jan</span>
                  <span className="text-[8px] text-gray-400" data-id="element-2486">Jun</span>
                  <span className="text-[8px] text-gray-400" data-id="element-2487">Dec</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </SlideWrapper>;
}
/* Slide 8: Trust & Compliance */
function SlideTrustContent() {
  const standards = [{
    name: 'EUDR',
    desc: 'EU Deforestation Regulation compliance for all agricultural exports',
    color: 'bg-blue-500'
  }, {
    name: 'Verra VCS',
    desc: 'Verified Carbon Standard for forestry, cookstove, and soil carbon credits',
    color: 'bg-emerald-500'
  }, {
    name: 'Gold Standard',
    desc: 'Premium carbon credits with SDG co-benefit verification',
    color: 'bg-amber-500'
  }, {
    name: 'UCDA',
    desc: 'Uganda Coffee Development Authority quality certification',
    color: 'bg-green-600'
  }, {
    name: 'ISO 14064',
    desc: 'International standard for greenhouse gas accounting and verification',
    color: 'bg-purple-500'
  }, {
    name: 'UNFCCC CDM',
    desc: 'Clean Development Mechanism methodology compliance',
    color: 'bg-sky-500'
  }];
  return <SlideWrapper bg="bg-carbon-deep" textColor="text-white" dark data-id="element-2488">
      <GridPattern id="trust-grid" opacity={0.03} data-id="element-2489" />
      <HexagonDecoration className="absolute -right-20 -top-20 w-80 h-80 text-carbon-bright" data-id="element-2490" />

      <div className="relative z-10 max-w-5xl mx-auto px-8 w-full" data-id="element-2491">
        <motion.div className="text-center mb-12" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2492">
          <p className="text-sm font-bold uppercase tracking-widest text-carbon-bright mb-3" data-id="element-2493">
            Trust & Compliance
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight" data-id="element-2494">
            Built on{' '}
            <span className="text-carbon-bright" data-id="element-2495">global standards</span>
          </h2>
          <p className="mt-4 text-lg text-white/50 max-w-2xl mx-auto" data-id="element-2496">
            Every certificate and credit is independently verified against the
            world's most rigorous frameworks.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2497">
          {standards.map((s, i) => <motion.div key={s.name} className="flex items-start gap-4 p-5 rounded-2xl bg-white/5 border border-white/10" initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1 + i * 0.07,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-2498">
              <div className={`w-10 h-10 rounded-xl ${s.color} flex items-center justify-center flex-shrink-0`} data-id="element-2499">
                <ShieldCheckIcon className="w-5 h-5 text-white" data-id="element-2500" />
              </div>
              <div data-id="element-2501">
                <h3 className="text-sm font-bold text-white" data-id="element-2502">{s.name}</h3>
                <p className="text-xs text-white/40 mt-1 leading-snug" data-id="element-2503">
                  {s.desc}
                </p>
              </div>
            </motion.div>)}
        </div>
      </div>
    </SlideWrapper>;
}
/* Slide 9: CTA */
function SlideCTAContent() {
  return <SlideWrapper bg="bg-white" dark={false} hideWatermark data-id="element-2504">
      <GridPattern id="cta-grid" opacity={0.03} data-id="element-2505" />
      <HexagonDecoration className="absolute -left-16 -top-16 w-72 h-72 text-carbon-bright" data-id="element-2506" />
      <HexagonDecoration className="absolute -right-12 -bottom-12 w-64 h-64 text-carbon-bright" data-id="element-2507" />
      <CircuitDecoration className="absolute right-1/4 top-1/4 w-[300px] h-[300px] text-carbon-bright" data-id="element-2508" />

      <div className="relative z-10 text-center max-w-3xl mx-auto px-8" data-id="element-2509">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-2510">
          <img src="https://f000.backblazeb2.com/file/jpgshared/f2RRyoGc" alt="CarbonSafe" className="h-12 mx-auto mb-8" data-id="element-2511" />
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-deep tracking-tight leading-[1.1]" data-id="element-2512">
            Every shipment tells a story.
            <br data-id="element-2513" />
            <span className="text-carbon-bright" data-id="element-2514">Make yours verified.</span>
          </h2>
          <p className="mt-6 text-lg text-carbon-mid max-w-xl mx-auto leading-relaxed" data-id="element-2515">
            Join producers, exporters, and project developers using CarbonSafe
            to unlock verified trade and carbon finance across Africa.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2516">
            <a href={MAILTO.presentationDemo} className="inline-flex items-center gap-2.5 px-8 py-4 bg-carbon-bright text-white text-base font-semibold rounded-lg hover:bg-carbon-light transition-colors shadow-lg shadow-carbon-bright/20" data-id="element-2517">
              Book a Demo
              <ArrowUpRightIcon className="w-4 h-4" data-id="element-2518" />
            </a>
            <a href={MAILTO.downloadDeck} className="inline-flex items-center gap-2.5 px-8 py-4 border-2 border-carbon-bright text-carbon-deep text-base font-semibold rounded-lg hover:bg-carbon-bright/5 transition-colors" data-id="element-2519">
              Download Deck (PDF)
            </a>
          </div>
          <p className="mt-10 text-sm text-carbon-mid/60" data-id="element-2520">
            operations@carbonsafe.earth · carbonsafe.earth
          </p>
        </motion.div>
      </div>
    </SlideWrapper>;
}
/* ─── Slide Registry ─── */
const slides = [SlideTitleContent, SlideProblemContent, SlideSolutionContent, SlideHowItWorksContent, SlideVerticalsContent, SlideMarketContent, SlidePlatformContent, SlideTrustContent, SlideCTAContent];
/* ─── Main Presentation Component ─── */
export function Presentation({
  onExit
}: PresentationProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= TOTAL_SLIDES || index === currentSlide) return;
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide]);
  const next = useCallback(() => goTo(currentSlide + 1), [currentSlide, goTo]);
  const prev = useCallback(() => goTo(currentSlide - 1), [currentSlide, goTo]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Escape') {
        onExit();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [next, prev, onExit]);
  const SlideContent = slides[currentSlide];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };
  return <div className="fixed inset-0 z-[100] bg-black" data-id="element-2521">
      {/* Slide Area */}
      <div className="relative w-full h-full overflow-hidden" data-id="element-2522">
        <AnimatePresence mode="wait" custom={direction} data-id="element-2523">
          <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="absolute inset-0" data-id="element-2524">
            <SlideContent data-id="element-2525" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Top bar: exit + slide counter */}
      <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4" data-id="element-2526">
        <button onClick={onExit} className="w-9 h-9 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/40 transition-colors" aria-label="Exit presentation" data-id="element-2527">
          <XIcon className="w-4 h-4 text-white" data-id="element-2528" />
        </button>
        <span className="text-xs font-bold text-white/40 tabular-nums tracking-wider bg-black/20 backdrop-blur-sm px-3 py-1.5 rounded-full border border-white/10" data-id="element-2529">
          {currentSlide + 1} / {TOTAL_SLIDES}
        </span>
      </div>

      {/* Bottom navigation */}
      <div className="absolute bottom-0 left-0 right-0 z-50 flex items-center justify-between px-6 py-4" data-id="element-2530">
        {/* Nav arrows */}
        <div className="flex items-center gap-2" data-id="element-2531">
          <button onClick={prev} disabled={currentSlide === 0} className="w-10 h-10 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Previous slide" data-id="element-2532">
            <ArrowLeftIcon className="w-4 h-4 text-white" data-id="element-2533" />
          </button>
          <button onClick={next} disabled={currentSlide === TOTAL_SLIDES - 1} className="w-10 h-10 rounded-full bg-black/20 backdrop-blur-sm border border-white/10 flex items-center justify-center hover:bg-black/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed" aria-label="Next slide" data-id="element-2534">
            <ArrowRightIcon className="w-4 h-4 text-white" data-id="element-2535" />
          </button>
        </div>

        {/* Dot indicators */}
        <div className="flex items-center gap-1.5" data-id="element-2536">
          {slides.map((_, i) => <button key={i} onClick={() => goTo(i)} className={`rounded-full transition-all duration-300 ${i === currentSlide ? 'w-8 h-2 bg-carbon-bright' : i < currentSlide ? 'w-2 h-2 bg-carbon-bright/40' : 'w-2 h-2 bg-white/20 hover:bg-white/30'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-2537" />)}
        </div>

        {/* Keyboard hint */}
        <span className="text-[10px] text-white/30 font-medium tracking-wider uppercase hidden sm:block" data-id="element-2538">
          ← → to navigate · Esc to exit
        </span>
      </div>
    </div>;
}